import React from 'react';
import { ArrowRight, Shield, Clock, Truck, Lock, Video, CheckCircle, MapPin, Briefcase } from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SelfStorage',
    name: 'Interskate Self Storage',
    description: 'Clean, secure indoor self storage with drive-up access in Lewisville, TX. Located on S Stemmons Fwy right off I-35. 24-hour access, individually coded gate, affordable month-to-month rates.',
    url: 'https://www.interskateselfstorage.com',
    telephone: '(469) 251-6255',
    email: 'interskate.selfstorage@gmail.com',
    image: 'https://www.interskateselfstorage.com/logo192.png',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1303 S Stemmons Fwy',
      addressLocality: 'Lewisville',
      addressRegion: 'TX',
      postalCode: '75067',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 33.0318,
      longitude: -97.0641
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '06:00',
        closes: '21:00'
      }
    ],
    priceRange: '$',
    amenityFeature: [
      { '@type': 'LocationFeatureSpecification', name: '24-Hour Access', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Drive-Up Access', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Electronic Gate', value: true },
      { '@type': 'LocationFeatureSpecification', name: '24/7 Video Surveillance', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Indoor Units', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Online Rentals', value: true }
    ],
    areaServed: [
      { '@type': 'City', name: 'Lewisville, TX' },
      { '@type': 'City', name: 'Flower Mound, TX' },
      { '@type': 'City', name: 'Highland Village, TX' },
      { '@type': 'City', name: 'Coppell, TX' },
      { '@type': 'City', name: 'Corinth, TX' },
      { '@type': 'City', name: 'Lake Dallas, TX' }
    ],
    paymentAccepted: 'Cash, Visa, Mastercard, American Express, Discover'
  };

  const features = [
    {
      icon: <Briefcase className="h-8 w-8 text-green-600" />,
      title: 'Commercial & Business Solutions',
      description: 'Secure inventory and equipment storage for local businesses.'
    },
    {
      icon: <Truck className="h-8 w-8 text-green-600" />,
      title: 'Drive-Up Convenience',
      description: 'Park at your door for easy loading of furniture and heavy boxes.'
    },
    {
      icon: <Truck className="h-8 w-8 text-green-600" />,
      title: 'Moving Truck Friendly',
      description: 'Wide drive aisles designed for U-Hauls and delivery vans.'
    },
    {
      icon: <Video className="h-8 w-8 text-green-600" />,
      title: 'Secure & Monitored',
      description: '24/7 video surveillance and electronic gate access.'
    },
    {
      icon: <Lock className="h-8 w-8 text-green-600" />,
      title: 'Online Rentals',
      description: 'Rent a unit from your phone in 2 minutes.'
    }
  ];

  const unitSizes = [
    {
      size: '5x10',
      sqft: '50 sq ft',
      ideal: 'Fits a Queen mattress, dresser, and boxes. (Great for decluttering).',
      comparable: 'Walk-in Closet'
    },
    {
      size: '10x10',
      sqft: '100 sq ft',
      ideal: 'Fits the contents of a 1-bedroom apartment.',
      comparable: 'Average Bedroom'
    },
    {
      size: '10x20',
      sqft: '200 sq ft',
      ideal: 'Fits the contents of a 3-bedroom home. (The "Home Mover" favorite).',
      comparable: 'Single-Car Garage'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Drive-Up Business & Home Storage in Lewisville, TX | Interskate Self Storage"
        description="Ground-level, drive-up storage units on I-35E in Lewisville, TX. No elevators, no stairs. Business, contractor, and household storage. Rent online in 2 minutes."
        canonical="/"
        keywords="self storage Lewisville TX, indoor storage Lewisville, storage near I-35, drive-up storage, cheap storage Lewisville, 24 hour storage, garage rental Lewisville"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section
        className="relative h-[600px] bg-cover bg-center hero-overlay"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600181914037-b14638c1137d)' }}
        data-testid="hero-section"
      >
        <div className="hero-content h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 animate-fadeInUp">
              Drive-Up Business & Home Storage
              <br />
              <span className="text-green-200">On I-35E</span>
            </h1>
            <p className="text-xl md:text-2xl text-white mb-4 max-w-3xl animate-fadeInUp">
              No Elevators. No Stairs. Just affordable, ground-level storage in Lewisville.
            </p>
            <p className="text-lg md:text-xl text-green-100 mb-8 max-w-3xl animate-fadeInUp">
              Stop hauling your gear up elevators. Park right at your door.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp">
              <a
                href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (window.gtag) {
                    window.gtag('event', 'conversion_intent', {
                      'event_category': 'lead',
                      'event_label': 'hero_rates_availability',
                      'facility_name': 'Interskate Self Storage'
                    });
                  }
                }}
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-xl btn-primary"
                data-testid="hero-rent-button"
              >
                See Rates & Availability
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-white hover:text-green-600 transition-all"
                data-testid="hero-contact-button"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Stop hauling your gear up elevators and down long hallways. At <strong>Interskate Self Storage</strong>,
              we make storage simple. Located directly on the I-35E service road (South Stemmons Fwy), we offer
              exclusively ground-level, drive-up storage units.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We are the affordable, hassle-free alternative to the big corporate chains. Whether you are a local
              contractor needing easy access to tools or a family clearing out the garage, you can pull your vehicle
              right up to your unit door.
            </p>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-20 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-green-600">Interskate Self Storage?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We focus on what matters most: convenience, security, and value.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md feature-card"
                data-testid={`feature-card-${index}`}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unit Sizes Preview */}
      <section className="py-20 bg-white" data-testid="unit-sizes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Popular <span className="text-green-600">Unit Sizes</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't pay for space you don't need. Find the perfect fit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {unitSizes.map((unit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                data-testid={`unit-size-${unit.size}`}
              >

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{unit.size}</h3>
                  <p className="text-green-600 font-semibold mb-2">{unit.sqft}</p>
                  <p className="text-sm text-gray-500 mb-2">≈ {unit.comparable}</p>
                  <p className="text-gray-600 text-sm">{unit.ideal}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="/size-guide"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 border-2 border-green-600 rounded-lg hover:bg-green-50 transition-all shadow-md mr-4"
              data-testid="size-guide-button"
            >
              See Full Size Guide
            </a>
            <a
              href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'conversion_intent', {
                    'event_category': 'lead',
                    'event_label': 'home_sizes_check_prices',
                    'facility_name': 'Interskate Self Storage'
                  });
                }
              }}
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-md btn-primary"
              data-testid="check-prices-button"
            >
              Check Today's Prices
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Military & First Responder Discount */}
      <section id="people-who-serve" className="scroll-mt-24 py-20 bg-gray-950 text-white" data-testid="military-discount-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6 text-green-500" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Honoring Those Who Serve
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Active & veteran military, first responders, and educators give so much to our
            community. We're grateful — and we'd like to give a little back.
          </p>
          <div className="inline-block bg-green-700 rounded-xl px-10 py-6 mb-6">
            <p className="text-3xl md:text-4xl font-bold text-white mb-2">
              10% Off Every Month
            </p>
            <p className="text-green-200 text-sm tracking-wide">
              Military &bull; Police &bull; Fire &bull; EMS &bull; Educators
            </p>
          </div>
          <p className="text-gray-400 text-sm">
            Just mention your service when you rent online or call us — we'll take care of the rest.
          </p>
        </div>
      </section>

      {/* Location Highlight */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MapPin className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Minutes from Lake Lewisville
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Conveniently located for all your storage needs. Whether you're decluttering your home or storing business inventory, we have the perfect space for you.
          </p>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Rent online in 2 minutes or call us for personalized help selecting the right unit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'conversion_intent', {
                    'event_category': 'lead',
                    'event_label': 'home_cta_rent_now',
                    'facility_name': 'Interskate Self Storage'
                  });
                }
              }}
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-lg btn-primary"
              data-testid="cta-rent-button"
            >
              Rent Online Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="tel:4692516255"
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-all shadow-lg"
              data-testid="cta-call-button"
            >
              Call (469) 251-6255
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
